//
// ExtensionGroupMenu for RecordInspectorExtensions
//
// Actions that apply to all records sharing this named extended definition.
//

import { observer } from 'mobx-react-lite'
import { useStore } from '../../../stores'

import { FixedVSpace, VScrollView, VView } from '../../../appview'
import {
    Header,
    Icon,
    Item,
    Menu,
    MenuDivider,
    MenuItem,
    Text,
} from '../../../components'
import { RecordDefinitionChangePanel, RecordExtensionPickPanel } from '../../../panels'
import { changeDefinitionLostDataFields } from '../../../stores/data/utils'
import { ItemTitle } from '../../components'

const ConfirmLostFieldsSubmenu = observer(function ConfirmLostFieldsSubmenu({
    title,
    body,
    confirmLabel,
    lostFields,
    onConfirm,
    onCancel,
}) {
    const { app } = useStore()
    return (
        <VView className="actions-panel recorddefinition-change-panel">
            <div className="inspector">
                {title ? <Header>{title}</Header> : undefined}
                <Header className="cc-danger">{app.text('Warning')}</Header>
                <Text>{body}</Text>
                {lostFields.length ? (
                    <>
                        <Text>
                            {app.text(
                                `For all records that use this definition,
                                 these fields will be lost:`
                            )}
                        </Text>
                        <VScrollView className="lost-fields">
                            {lostFields.map(field => (
                                <Item key={field.gid}>
                                    <Icon name="field" size="text" />{' '}
                                    <ItemTitle item={field} ellipsisAfter={32} />
                                </Item>
                            ))}
                        </VScrollView>
                        <FixedVSpace size={5} />
                    </>
                ) : undefined}
            </div>
            <Menu>
                <MenuItem onClick={onCancel}>{app.text('Cancel')}</MenuItem>
                <MenuItem className="cc-danger" onClick={onConfirm}>
                    {confirmLabel}
                </MenuItem>
            </Menu>
        </VView>
    )
})

export const ExtensionGroupMenu = observer(function ExtensionGroupMenu({
    worksheet,
    onAction,
}) {
    const { app, data } = useStore()

    const _onAction = (action, parameter) => {
        onAction && onAction(action, parameter)
    }

    const record = worksheet.record
    const definition = data.definitions.get(record.definition)
    const original_definition =
        definition && definition.is_extended
            ? data.definitions.get(definition.original)
            : null
    const extensionName = definition ? definition.name : ''

    const lostFields =
        original_definition && definition
            ? changeDefinitionLostDataFields(original_definition, definition, data)
            : []

    if (record.is_link) {
        return (
            <VView className="actions-panel recorddefinition-change-panel">
                <div className="inspector">
                    <Text>
                        {app.text(
                            `Extension changes are not available for linked records.`
                        )}
                    </Text>
                </div>
                <Menu>
                    <MenuItem onClick={() => _onAction('cancel')}>
                        {app.text('Ok')}
                    </MenuItem>
                </Menu>
            </VView>
        )
    }

    return (
        <VView className="actions-panel recorddefinition-change-panel">
            <div className="inspector">
                <Header>{app.text('Extension group')}</Header>
                <Text className="cc-notice">
                    {app.text(
                        `For all siblings that share the '{extensionName}'
                         extension`,
                        {
                            extensionName,
                        }
                    )}
                </Text>
            </div>
            <Menu>
                <MenuItem
                    submenu={
                        <RecordExtensionPickPanel
                            worksheet={worksheet}
                            title={app.text('Transfer to')}
                            description={app.text(
                                `Siblings in this group become part of the other
                                 extension group. The current extension group is cleaned
                                 up when unused.`
                            )}
                            excludeCurrent={true}
                            onlyExtended={false}
                            alwaysConfirm={true}
                            confirmLabel={app.text('Yes, transfer to')}
                            onPick={definition_gid =>
                                _onAction('transfer_to', definition_gid)
                            }
                            onCancel={() => _onAction('cancel')}
                        />
                    }
                >
                    {app.text('Transfer to …')}
                </MenuItem>
                <MenuItem
                    submenu={
                        <RecordExtensionPickPanel
                            worksheet={worksheet}
                            title={app.text('Transfer from')}
                            description={app.text(
                                `Siblings from the other extension group become part of
                                 this group. Their previous extension group is cleaned
                                 up.`
                            )}
                            excludeCurrent={true}
                            onlyExtended={false}
                            currentIsTarget={true}
                            alwaysConfirm={true}
                            confirmLabel={app.text('Yes, transfer from')}
                            onPick={definition_gid =>
                                _onAction('transfer_from', definition_gid)
                            }
                            onCancel={() => _onAction('cancel')}
                        />
                    }
                >
                    {app.text('Transfer from …')}
                </MenuItem>
                <MenuDivider />
                <MenuItem
                    className="cc-notice"
                    submenu={
                        <ConfirmLostFieldsSubmenu
                            title={app.text('Clear')}
                            body={app.text(
                                `Removes all classes, fields, and fieldlists from
                                 the extension group, but keeps the name.`
                            )}
                            confirmLabel={app.text('Yes, clear')}
                            lostFields={lostFields}
                            onConfirm={() => _onAction('clear_extension')}
                            onCancel={() => _onAction('cancel')}
                        />
                    }
                >
                    {app.text('Clear')}
                </MenuItem>
                <MenuItem
                    submenu={
                        <RecordExtensionPickPanel
                            worksheet={worksheet}
                            title={app.text('Clone from')}
                            description={app.text(
                                `Keeps the current extension group name and gid,
                                 but overwrites the base definition, classes, fields and
                                 fieldlists with those from another extension group. `
                            )}
                            excludeCurrent={true}
                            onlyExtended={true}
                            alwaysConfirm={true}
                            confirmLabel={app.text('Yes, clone from')}
                            onPick={definition_gid =>
                                _onAction('clone_from', definition_gid)
                            }
                            onCancel={() => _onAction('cancel')}
                        />
                    }
                >
                    {app.text('Clone from …')}
                </MenuItem>
                <MenuItem
                    className="cc-notice"
                    submenu={
                        <RecordDefinitionChangePanel
                            worksheet={worksheet}
                            title={app.text('Change base definition')}
                            keepExtensionExtras={true}
                            alwaysConfirm={true}
                            onChangeDefinition={definition_gid =>
                                _onAction('change_base_definition', definition_gid)
                            }
                            onCancel={() => _onAction('cancel')}
                        />
                    }
                >
                    {app.text('Change base definition …')}
                </MenuItem>
                <MenuDivider />
                <MenuItem
                    className="cc-notice"
                    submenu={
                        <ConfirmLostFieldsSubmenu
                            title={app.text('Drop extension group')}
                            body={app.text(
                                `All siblings in this extension group revert to the
                                 original definition. The extension group is cleaned
                                 up.`
                            )}
                            confirmLabel={app.text('Yes, drop extension group')}
                            lostFields={lostFields}
                            onConfirm={() => _onAction('drop_group_extension')}
                            onCancel={() => _onAction('cancel')}
                        />
                    }
                >
                    {app.text('Drop extension group')}
                </MenuItem>
            </Menu>
        </VView>
    )
})
